<script lang="ts" module>
	interface PreloadFont {
		type: string;
		href: string;
	}
</script>

<script lang="ts">
	import defaultOgImage from "@assets/images/og-image.jpg";
	import { createTitle } from "@utils/createTitle.js";
	import type { Snippet } from "svelte";
	import resolveConfig from "tailwindcss/resolveConfig.js";
	import InterVariable from "@core/assets/fonts/Inter/Inter-VariableFont_opsz,wght.woff2";
	import SyneVariable from "@core/assets/fonts/Syne/Syne-VariableFont_wght.woff2";
	import { tenants } from "@core/config/tenants.js";
	import type { Media } from "@core/schema/Media.js";
	import { appName } from "@core/schema/names/appName.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import tailwindConfig from "../../../tailwind.config.js";
	import { SvelteTemplate } from "../../Template.js";
	import "../../assets/css/main.css";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import Footer from "./Footer.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import Header from "./Header.svelte";
	import PageDecoration from "./PageDecoration.svelte";
	import Tracking from "./Tracking.svelte";
	import { pageDecorations } from "./pageDecorations.js";

	const {
		ogImage = defaultOgImage,
		children,
	}: {
		ogImage?: string | Media;
		children: Snippet;
	} = $props();

	const { ROBOTS, WEB_URL, FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const {
		breadcrumbs: _breadcrumbs,
		pagePath,
		title,
		description,
		keywords,
		menu,
		index,
		tenant,
		template,
	} = getGlobalTemplateProps();

	const currentPath = pagePath[tenant.locale] ?? "/";

	const decoration = $derived.by(() => {
		if (template === undefined) {
			return "none";
		}
		return pageDecorations[template];
	});

	const pageTitle = createTitle(title);

	const fontsToPreload: PreloadFont[] = [
		{ href: InterVariable, type: "font/woff2" },
		{ href: SyneVariable, type: "font/woff2" },
	];
	const canonical = tenant.locale in pagePath ? `${WEB_URL}${pagePath[tenant.locale]}` : undefined;
	const themeColor = resolveConfig(tailwindConfig).theme.colors.white;
	const ogImagePath = $derived(
		typeof ogImage === "string" ? ogImage : thumbUploadedFile(1200, 630, ogImage, FILE_BUCKET_URL_MAP, null),
	);

	let savedScrollY = 0;
	let hasBeenLocked = false;

	$effect(() => {
		if (lockScroll.lock) {
			savedScrollY = window.scrollY;
			document.documentElement.style.overflow = "clip";
			document.body.style.overflow = "clip";
			document.body.style.position = "fixed";
			document.body.style.top = `-${savedScrollY}px`;
			document.body.style.left = "0";
			document.body.style.right = "0";
			hasBeenLocked = true;
		} else if (hasBeenLocked) {
			const y = savedScrollY;
			document.body.style.position = "";
			document.body.style.top = "";
			document.body.style.left = "";
			document.body.style.right = "";
			document.body.style.overflow = "";
			document.documentElement.style.overflow = "";
			window.scrollTo({ top: y, behavior: "instant" });
			hasBeenLocked = false;
		}
	});
</script>

<svelte:head>
	<!-- Preloads of fonts used at the top of pages -->
	{#each fontsToPreload as { href, type } (href)}
		<link rel="preload" as="font" {href} {type} crossorigin="anonymous" />
	{/each}

	<!-- Theme color -->
	<meta name="msapplication-TileColor" content={themeColor} />
	<meta name="theme-color" content={themeColor} />

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	{#if canonical}
		<meta property="og:url" content={canonical} />
	{/if}
	<meta property="og:image" content={ogImagePath} />

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL} />
	{#if canonical}
		<meta property="twitter:url" content={canonical} />
	{/if}
	<meta name="twitter:image" content={ogImagePath} />

	<!-- Title, description and keywords -->
	<title>{pageTitle}</title>
	<meta property="title" content={pageTitle} />
	<meta property="og:title" content={pageTitle} />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />
	{#if keywords}
		<meta name="keywords" content={keywords} />
	{/if}

	<!-- Robots -->
	<meta name="robots" content={ROBOTS === "index" && index ? "index, follow" : "noindex, nofollow"} />

	<!-- Canonical and hreflang -->
	{#if canonical}
		<link rel="canonical" href={canonical} />
	{/if}
	{#each Object.values(tenants) as tenant (tenant.locale)}
		{#if tenant.locale in pagePath}
			<link rel="alternate" hreflang={tenant.locale} href="{WEB_URL}{pagePath[tenant.locale]}" />
		{/if}
	{/each}
</svelte:head>

<HeadFavicons />

<Tracking />

<div class="relative" class:overflow-x-clip={decoration === "rounded"}>
	<PageDecoration {decoration} />
	<div class="contents">
		<Header {menu} {currentPath} />

		<main class="grid-container grow relative">
			{@render children()}
		</main>

		<Footer isHomepage={template === SvelteTemplate.Homepage} />
	</div>
</div>
